import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './GrammarExercisesPage.css';

const GrammarExercisesPage = () => {
  const { t, i18n } = useTranslation();
  const [selectedType, setSelectedType] = useState('prepositions');
  const [topic, setTopic] = useState('');
  const [topicSource, setTopicSource] = useState('custom'); // 'custom' | 'topicOfTheDay'
  const [isGenerating, setIsGenerating] = useState(false);
  const [exercises, setExercises] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [isMarking, setIsMarking] = useState(false);
  const [markingResults, setMarkingResults] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [topicOfTheDay, setTopicOfTheDay] = useState(null);
  const [loadingTopic, setLoadingTopic] = useState(true);
  const [showTopicPreview, setShowTopicPreview] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Reset component state when component mounts (for direct URL access)
  useEffect(() => {
    // Reset to initial state when component is accessed directly
    setExercises(null);
    setUserAnswers({});
    setMarkingResults({});
    setShowResults(false);
    setTopic('');
    setIsGenerating(false);
    setIsMarking(false);
  }, []);

  // Fetch Topic of the Day
  useEffect(() => {
    fetchTopicOfTheDay();
  }, []);

  const fetchTopicOfTheDay = async () => {
    try {
      setLoadingTopic(true);
      const response = await fetch('/api/topic-of-the-day');
      if (response.ok) {
        const data = await response.json();
        setTopicOfTheDay(data);
      } else {
        // Fallback topic if API fails
        setTopicOfTheDay({
          topic: 'Technology & Innovation',
          description: 'Explore the latest developments in technology and their impact on society',
          generatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Failed to fetch topic of the day:', error);
      // Fallback topic
      setTopicOfTheDay({
        topic: 'Environmental Sustainability',
        description: 'Learn about environmental challenges and sustainable solutions',
        generatedAt: new Date().toISOString()
      });
    } finally {
      setLoadingTopic(false);
    }
  };

  const handleTopicOfTheDaySelect = () => {
    if (topicOfTheDay) {
      setTopic(topicOfTheDay.topic);
      setTopicSource('topicOfTheDay');
      setShowSelectionModal(true);
    }
  };

  const grammarTypes = [
    { value: 'prepositions', label: 'Prepositions' },
    { value: 'present_perfect', label: 'Present Perfect Tense' },
    { value: 'past_perfect', label: 'Past Perfect Tense' },
    { value: 'future_tenses', label: 'Future Tenses' },
    { value: 'articles', label: 'Articles (a/an/the)' },
    { value: 'modal_verbs', label: 'Modal Verbs' },
    { value: 'conditionals', label: 'Conditionals' },
    { value: 'passive_voice', label: 'Passive Voice' },
    { value: 'reported_speech', label: 'Reported Speech' },
    { value: 'mixed', label: 'Mixed Grammar Review' }
  ];

  const handleGenerate = async () => {
    if (!topic.trim()) {
      setErrorMessage('Please enter a topic for your exercises.');
      setShowErrorModal(true);
      return;
    }

    setIsGenerating(true);
    setExercises(null);
    setUserAnswers({});
    setMarkingResults({});
    setShowResults(false);

    try {
      const response = await fetch('/api/generate-grammar-exercises', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grammarType: selectedType,
          topic: topic.trim()
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to generate exercises');
      }

      setExercises(data.exercises);
    } catch (error) {
      console.error('Error generating exercises:', error);
      setErrorMessage(error.message || 'Failed to generate exercises. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = async () => {
    const answeredCount = Object.keys(userAnswers).length;
    if (answeredCount === 0) {
      setErrorMessage('Please answer at least one question before submitting.');
      setShowErrorModal(true);
      return;
    }

    setIsMarking(true);
    setShowResults(false);

    try {
      const response = await fetch('/api/mark-grammar-answers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exercises: exercises,
          userAnswers: userAnswers,
          grammarType: selectedType,
          topic: topic
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to mark answers');
      }

      setMarkingResults(data.results);
      setShowResults(true);
    } catch (error) {
      console.error('Error marking answers:', error);
      setErrorMessage('Failed to mark answers. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsMarking(false);
    }
  };

  const handleReset = () => {
    setExercises(null);
    setUserAnswers({});
    setMarkingResults({});
    setShowResults(false);
    setTopic('');
  };

  const getGrammarTypeLabel = (value) => {
    return grammarTypes.find(type => type.value === value)?.label || value;
  };

  if (exercises) {
    return (
      <div className="grammar-exercises-page">
        {/* Marking Loading Popup */}
        {isMarking && (
          <div className="marking-loading-overlay">
            <div className="marking-loading-popup">
              <div className="loading-spinner"></div>
              <h3>🤖 PaperTips AI is marking your answers...</h3>
              <p>Please wait while our AI analyzes your responses and provides personalized feedback.</p>
            </div>
          </div>
        )}

        <div className="grammar-header">
          <h1>Grammar Exercises: {getGrammarTypeLabel(selectedType)}</h1>
          <p className="grammar-topic">Topic: {topic}</p>
        </div>

        <div className="exercises-container">
          <div className="exercises-instructions">
            <h3>Instructions:</h3>
            <p>{exercises.instructions}</p>
          </div>

          <div className="questions-section">
            <h3>Questions ({exercises.questions.length})</h3>
            {exercises.questions.map((question, index) => (
              <div key={question.id} className="question-item">
                <div className="question-header">
                  <span className="question-number">Q{index + 1}</span>
                </div>
                <div className="question-text">{question.text}</div>
                <div className="answer-input">
                  <input
                    type="text"
                    placeholder="Type your answer..."
                    value={userAnswers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    className="answer-field"
                    disabled={showResults}
                  />
                  {/* Show correct/incorrect assessment inline after submit */}
                  {showResults && markingResults[question.id] && (
                    <div className="assessment-indicator">
                      {markingResults[question.id]?.isCorrect ? (
                        <span className="correct-indicator">✅ Correct</span>
                      ) : (
                        <span className="incorrect-indicator">❌ Incorrect</span>
                      )}
                    </div>
                  )}
                  
                  {/* Show model answer and feedback inline after submit */}
                  {showResults && markingResults[question.id] && (
                    <div className="inline-feedback">
                      <div className="feedback-text">
                        <strong>Feedback:</strong> {markingResults[question.id]?.feedback || 'No feedback available'}
                      </div>
                      <div className="model-answer-card">
                        <strong>Model Answer:</strong> {markingResults[question.id]?.correctAnswer || 'N/A'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="action-buttons">
            <button 
              onClick={handleSubmit} 
              className="submit-btn"
              disabled={isMarking}
            >
              {isMarking ? 'Marking...' : 'Submit Answers'}
            </button>
          </div>
        </div>

        {/* Generate New Set Button - Outside the exercises block */}
        <div className="generate-new-set-section">
          <button onClick={handleReset} className="generate-new-set-btn">
            🔄 Generate New Set
          </button>
          <p className="generate-new-set-hint">Create a fresh set of grammar exercises with different content</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grammar-exercises-page">
      {/* Generation Loading Popup */}
      {isGenerating && (
        <div className="generation-loading-overlay">
          <div className="generation-loading-popup">
            <div className="loading-spinner"></div>
            <h3>🤖 PaperTips AI is generating your exercises...</h3>
            <p>Please wait while our AI creates personalized grammar exercises tailored to your topic.</p>
          </div>
        </div>
      )}

      {/* Marking Loading Popup */}
      {isMarking && (
        <div className="marking-loading-overlay">
          <div className="marking-loading-popup">
            <div className="loading-spinner"></div>
            <h3>🤖 PaperTips AI is marking your answers...</h3>
            <p>Please wait while our AI analyzes your responses and provides personalized feedback.</p>
          </div>
        </div>
      )}

      <div className="grammar-hero">
        <h1>Grammar Practice</h1>
        <p>Master English grammar with AI-generated exercises tailored to your needs!</p>
      </div>

      <div className="grammar-form-container">
        <div className="grammar-form">
          <h2>Generate Your Exercises</h2>
          
          <div className="form-group">
            <label htmlFor="grammarType">Grammar Type:</label>
            <select
              id="grammarType"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="grammar-select"
            >
              {grammarTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Topic of the Day Section */}
          <div className="form-group topic-of-day-section">
            <label className="section-label">🎯 Topic of the Day</label>
            {loadingTopic ? (
              <div className="topic-of-the-day-loading" aria-live="polite">
                <div className="loading-spinner" aria-hidden="true"></div>
                <p>Loading today's topic...</p>
              </div>
            ) : topicOfTheDay ? (
                          <div className="topic-of-day-card" onClick={() => setShowTopicPreview(true)} style={{ cursor: 'pointer' }}>
              <div className="topic-of-day-content">
                <div className="topic-preview-section">
                  <div className="topic-preview-badge">
                    <span className="preview-topic">{topicOfTheDay.topic}</span>
                    {topicOfTheDay.description && (
                      <p className="topic-description">{topicOfTheDay.description}</p>
                    )}
                  </div>
                </div>
                
                <button
                  type="button"
                  className="topic-of-day-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTopicOfTheDaySelect();
                  }}
                  aria-label={`Use today's topic: ${topicOfTheDay.topic}`}
                >
                  🚀 Use Today's Topic
                </button>
              </div>
            </div>
            ) : (
              <div className="topic-of-day-error" role="alert">
                <p>Unable to load today's topic. Please try again or enter a custom topic below.</p>
              </div>
            )}
            <p className="topic-hint">Fresh topic generated daily at 6:00 AM (UTC+8)</p>
          </div>

          {/* Separator */}
          <div className="topic-separator">
            <div className="separator-line"></div>
            <div className="separator-text">or</div>
            <div className="separator-line"></div>
          </div>

          {/* Custom Topic Section */}
          <div className="form-group">
            <label htmlFor="topic">Topic/Theme:</label>
            <div className="topic-input-container">
              <input
                type="text"
                id="topic"
                value={topic}
                onChange={(e) => {
                  setTopic(e.target.value);
                  setTopicSource('custom');
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && topic.trim()) {
                    setShowSelectionModal(true);
                  }
                }}
                placeholder="e.g., Travel, Technology, Environment, Daily Life..."
                className="topic-input"
              />
              {topic.trim() && (
                <button
                  type="button"
                  className="show-selection-btn"
                  onClick={() => setShowSelectionModal(true)}
                >
                  ✓
                </button>
              )}
            </div>
            <small>Enter a custom topic or use today's topic above</small>
          </div>

          <button
            onClick={handleGenerate}
            disabled={isGenerating || !topic.trim()}
            className="generate-btn"
          >
            {isGenerating ? 'Generating Exercises...' : 'Generate Exercises'}
          </button>

          <div className="grammar-info">
            <h3>What You'll Get:</h3>
            <ul>
              <li>📝 10-15 focused grammar questions</li>
              <li>🎯 Questions tailored to your selected grammar type</li>
              <li>📚 Context-based on your chosen topic</li>
              <li>✅ Instant AI marking and feedback</li>
              <li>📊 Detailed score breakdown</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Topic Preview Popup */}
      {showTopicPreview && (
        <div className="topic-preview-overlay" onClick={() => setShowTopicPreview(false)}>
          <div className="topic-preview-popup" onClick={(e) => e.stopPropagation()}>
            <button 
              className="topic-preview-close" 
              onClick={() => setShowTopicPreview(false)}
              aria-label="Close topic preview"
            >
              ×
            </button>
            <div className="topic-preview-content">
              <h3>🎯 Today's Topic</h3>
              {topicOfTheDay && (
                <>
                  <div className="topic-preview-main">
                    <h4>{topicOfTheDay.topic}</h4>
                    <p>{topicOfTheDay.description}</p>
                  </div>
                  <div className="topic-preview-actions">
                    <button 
                      className="use-topic-btn"
                      onClick={() => {
                        handleTopicOfTheDaySelect();
                        setShowTopicPreview(false);
                      }}
                    >
                      🚀 Use This Topic
                    </button>
                    <button 
                      className="generate-btn modal-generate"
                      onClick={() => {
                        handleTopicOfTheDaySelect();
                        setShowTopicPreview(false);
                        handleGenerate();
                      }}
                      disabled={isGenerating}
                    >
                      {isGenerating ? 'Generating...' : 'Generate Exercises'}
                    </button>
                    <button 
                      className="close-preview-btn"
                      onClick={() => setShowTopicPreview(false)}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selection Confirmation Modal */}
      {showSelectionModal && (
        <div className="selection-modal-overlay" onClick={() => setShowSelectionModal(false)}>
          <div className="selection-modal" onClick={e => e.stopPropagation()}>
            <button type="button" className="selection-modal-close" onClick={() => setShowSelectionModal(false)} aria-label="Close selection modal">×</button>
            <div className="selection-modal-content">
              <h3>Selected Topic</h3>
              <div className="selected-topic-display modal-compact">
                <div className="selected-topic">
                  <span className="topic-text">{topic}</span>
                  <button
                    type="button"
                    className="clear-topic-btn"
                    onClick={() => {
                      setTopic('');
                      setTopicSource('custom');
                      setShowSelectionModal(false);
                    }}
                    aria-label="Clear selected topic"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <button 
                onClick={() => {
                  handleGenerate();
                  setShowSelectionModal(false);
                }}
                disabled={isGenerating || !topic.trim()}
                className="generate-btn modal-generate"
              >
                {isGenerating ? 'Generating...' : 'Generate Exercises'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="error-modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="error-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="error-modal-close" 
              onClick={() => setShowErrorModal(false)}
              aria-label="Close error modal"
            >
              ×
            </button>
            <div className="error-modal-content">
              <div className="error-icon">⚠️</div>
                            <h3>Failed to Generate Study Guide</h3>
              <p>{errorMessage}</p>
              <div className="error-modal-actions">
                <button
                  className="try-again-btn"
                  onClick={() => {
                    setShowErrorModal(false);
                    handleGenerate();
                  }}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : '🔄 Try Again'}
                </button>
                <button
                  className="discord-btn"
                  onClick={() => {
                    // Open Discord support server
                    window.open('https://discord.gg/papertips', '_blank');
                  }}
                >
                  💬 Discord Support
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrammarExercisesPage;
